import classNames from 'classnames/bind';
import { TimeCategory, areaCode } from 'common/modal/chart/utilChart';
import Toast, { useToast } from 'common/searchBox/Toast';
import SelectSeat from 'common/searchBox/selectSeat';
import { DateFormat } from 'common/types';
import { days } from 'common/utils';
import { Modal, ModalFuncContext, ModalPosition } from 'contexts/modalFunc';
import {
  ICity,
  IPassengers,
  SearchType,
  SeatClass,
  TripType,
  getLinkPair,
  getPassengerDesc,
  getSeatDesc,
} from 'contexts/searchParams';
import { useRouter } from 'next/router';
import { Fragment, useContext, useState } from 'react';
import ChartBar from './chartBar';
import styles from './index.module.scss';
import TopInfoBox from './info';

const cx = classNames.bind(styles);

function getPassengerSeatDesc(passengers: IPassengers, seatType: string) {
  return `${getPassengerDesc(passengers)}, ${getSeatDesc(true, seatType)}`;
}

function getMakeItinerary(
  tripType: TripType,
  departureCity: ICity,
  arrivalCity: ICity,
  departureDate: string,
  returnDate: string,
) {
  let itineraryList = [
    {
      departureCity: { ...departureCity, isDomestic: true },
      arrivalCity: { ...arrivalCity, isDomestic: true },
      departureDate: departureDate,
    },
  ];
  // 편도
  if (tripType === TripType.OW) return itineraryList;
  // 왕복
  return [
    ...itineraryList,
    {
      departureCity: { ...arrivalCity, isDomestic: true },
      arrivalCity: { ...departureCity, isDomestic: true },
      departureDate: returnDate,
    },
  ];
}

const ChartDomestic = (props: any) => {
  const {
    tourDepartureDate,
    tourReturnDate,
    departureCity,
    arrivalCity,
    tripType = TripType.RT,
    passengers: passengersCtx = {
      adult: 1,
      child: 0,
      infant: 0,
    },
    seatClass: seatClassCtx = SeatClass.Y,
  } = props.payload;

  // 가는날 출발날짜
  const [departureStartDate, setDepartureStartDate] =
    useState(tourDepartureDate);
  // 오는날 출발날짜. 한꺼번에 저장시 마지막것만 설정되어 분리
  const [returnStartDate, setReturnStartDate] = useState(tourReturnDate);
  // 필터링 출발날짜
  const [departureTimeList, setDepartureTimeList] = useState([
    [TimeCategory.DAY], // 가는편
    [TimeCategory.DAY], // 오는편
  ]);

  const setModal = useContext(ModalFuncContext);

  const router = useRouter();
  const [message, showToast] = useToast();
  const [showSeatOption, toggleSeatOption] = useState(false);
  const [passengers, setPassengerState] = useState(passengersCtx);
  const [seatClass, setSeatClassState] = useState(seatClassCtx);
  const [departureMouseOverDate, setDepartureMouseOverDate] = useState<
    string | null
  >(null);
  const [returnMouseOverDate, setReturnMouseOverDate] = useState<string | null>(
    null,
  );

  // 출발 날짜 설정
  const applyDepartureStartDate = (isDeparture: boolean, date: string) => {
    if (isDeparture) {
      setDepartureStartDate(date);
    } else {
      setReturnStartDate(date);
    }
  };

  // 마우스오버 날짜 설정
  const applyMouseOverDate = (isDeparture: boolean, date: string) => {
    if (isDeparture) {
      setDepartureMouseOverDate(date);
    } else {
      setReturnMouseOverDate(date);
    }
  };

  // 출발시간 적용
  const applyDepartureTimeList = (
    isDeparture: boolean,
    list: TimeCategory[],
  ) => {
    const [departure, arrival] = departureTimeList;
    const result = isDeparture ? [list, arrival] : [departure, list];
    setDepartureTimeList(result);
  };

  const setPassengers = (personType: string, diff: number) => {
    const newPassengers = {
      ...passengers,
      [personType]: passengers[personType] + diff,
    };
    if (newPassengers.adult < newPassengers.infant) {
      newPassengers.infant = newPassengers.adult;
    }
    setPassengerState(newPassengers);
  };

  const setSeatClass = (sc: SeatClass) => {
    setSeatClassState(sc);
  };

  const searchFlights = () => {
    if (
      tripType === TripType.RT &&
      // 오는날이 가는날보다 빠르면
      days(departureStartDate).diff(returnStartDate, 'day') > 0
    ) {
      showToast('여행 일정을 다시 선택해주세요.');
      return;
    }
    const itineraryList = getMakeItinerary(
      tripType,
      { ...departureCity, isDomestic: true },
      { ...arrivalCity, isDomestic: true },
      days(departureStartDate).format(DateFormat.YMD),
      tripType === TripType.RT
        ? days(returnStartDate).format(DateFormat.YMD)
        : '',
    );
    const [href, as] = getLinkPair(
      itineraryList,
      passengers,
      seatClass,
      false, // isDirect 옵션
      SearchType.DOMESTIC,
    );
    setModal({ type: Modal.NULL, position: ModalPosition.MAIN });
    router.push(href, as, { shallow: false });
  };

  return (
    <div className={cx('chart')}>
      <TopInfoBox
        position="grp"
        isDeparture={true} // 가는날
        applyDepartureTimeList={applyDepartureTimeList}
        departureTimeList={departureTimeList[0]}
        departureStartDate={departureMouseOverDate || departureStartDate}
        departureCity={departureCity}
        arrivalCity={arrivalCity}
      />
      <ChartBar
        trip={tripType}
        isDeparture={true} // 가는날
        departureCity={departureCity}
        arrivalCity={arrivalCity}
        departureTimeList={departureTimeList[0]}
        applyDepartureStartDate={applyDepartureStartDate}
        departureDate={tourDepartureDate}
        applyMouseOverDate={applyMouseOverDate}
      />
      {tripType === TripType.RT && (
        <Fragment>
          <TopInfoBox
            position="grp"
            isDeparture={false} // 오는날
            applyDepartureTimeList={applyDepartureTimeList}
            departureTimeList={departureTimeList[1]}
            departureStartDate={returnMouseOverDate || returnStartDate}
            departureCity={arrivalCity}
            arrivalCity={departureCity}
          />
          <ChartBar
            trip={tripType}
            isDeparture={false} // 오는날
            departureCity={arrivalCity}
            arrivalCity={departureCity}
            departureTimeList={departureTimeList[1]}
            applyDepartureStartDate={applyDepartureStartDate}
            departureDate={tourReturnDate}
            applyMouseOverDate={applyMouseOverDate}
          />
        </Fragment>
      )}
      <div className={cx('chart_query')}>
        <div className={cx('conditions')}>
          <button type="button" className={cx('condition', 'as_date')}>
            {days(departureMouseOverDate || departureStartDate).format(
              DateFormat.DotMDd,
            )}
          </button>
          {tripType === TripType.RT && (
            <button type="button" className={cx('condition', 'as_date')}>
              {days(returnMouseOverDate || returnStartDate).format(
                DateFormat.DotMDd,
              )}
            </button>
          )}
        </div>
        <div className={cx('conditions')}>
          <button
            type="button"
            className={cx('condition', 'as_seat')}
            aria-expanded={showSeatOption}
            data-event-nlog="click"
            data-event-area-code={`${areaCode}.passeat`}
            onClick={() => toggleSeatOption(!showSeatOption)}
          >
            {getPassengerSeatDesc(passengers, seatClass)}
          </button>
          {showSeatOption && (
            <div className={cx('select_seat')}>
              <SelectSeat
                isDomestic={true}
                passengers={passengers}
                seatClass={seatClass}
                tripType={tripType}
                setPassengers={setPassengers}
                setSeatClass={setSeatClass}
                toggleSeatOption={toggleSeatOption}
              />
            </div>
          )}
        </div>
        <button
          type="button"
          className={cx('search')}
          data-event-nlog="click"
          data-event-area-code={`${areaCode}.search`}
          onClick={searchFlights}
        >
          항공권 검색
        </button>
      </div>
      <Toast message={message} />
    </div>
  );
};

export default ChartDomestic;
